import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextStyle from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import { Extension } from "@tiptap/core";
import { splitListItem } from "prosemirror-schema-list";
import {
  setIsNovaChatBotOpen,
  setNovaInitialPrompt,
} from "store/AiFeatures/GoalsCreateWithNovaAI/action";
import useApplicationTracker from "customHooks/useApplicationTracker";

const useNovaAiModal = (props) => {
  const {
    conversationId,
    sendChatMessage,
    fetchAllConversations,
    newConversationId,
    setNewConversationId,
  } = props;
  const dispatch = useDispatch();
  const draggleRef = useRef(null);
  const { accountId } = useSelector((state) => state.user);
  const { conversationMessages, isNewConversation, initialPrompt } =
    useSelector((state) => state?.aiFeatures?.goalsCreateWithNovaAI || {});
  const { captureUserEvent } = useApplicationTracker();
  const [disabled, setDisabled] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [messageText, setMessageText] = useState("");
  const [sentMessageText, setSentMessageText] = useState("");
  const [page, setPage] = useState(1);
  const [isConversationChanged, setisConversationChanged] = useState(false);
  const [showUserMessage, setShowUserMessage] = useState(false);
  const [localMessages, setLocalMessages] = useState([]);
  const [scrollToBottom, setScrollToBottom] = useState(false);

  const handleClose = () => {
    dispatch(setIsNovaChatBotOpen(false));
  };
  const handleBackToChat = () => {
    setShowHistory(false);
    setPage(1);
    setScrollToBottom(true);
  };
  const handleShowHistory = () => {
    setShowHistory(true);
    captureUserEvent("nova_history_icon_clicked", {
      accountId: accountId,
    });
  };

  const handleSend = (text) => {
    const updatedId = isConversationChanged
      ? newConversationId
      : conversationId;
    setShowUserMessage(true);
    setSentMessageText(text);
    setMessageText("");
    setLocalMessages((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        content: text,
        role: "user",
        isLoading: true,
      },
    ]);
    dispatch(sendChatMessage({ conversationId: updatedId, userMessage: text }));
    setScrollToBottom(true);
  };

  const CustomKeymap = Extension.create({
    name: "customKeymap",

    addKeyboardShortcuts() {
      return {
        Enter: () => {
          return true;
        },
        "Shift-Enter": ({ editor }) => {
          const { state, dispatch } = editor.view;
          const { schema, selection } = state;
          const { $from } = selection;
          if (editor.isActive("bulletList") || editor.isActive("orderedList")) {
            const listItemType = schema.nodes.listItem;
            return splitListItem(listItemType)(state, dispatch);
          }
          const br = schema.nodes.hardBreak.create();
          const tr = state.tr.insert($from.pos, br);
          dispatch(tr.scrollIntoView());
          return true;
        },
      };
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: {},
      }),
      Placeholder.configure({
        placeholder: "How can I help you?",
      }),
      Link.configure({
        HTMLAttributes: {
          class: "my-custom-class",
        },
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
      TextStyle,
      Color,
      CustomKeymap,
    ],
    content: messageText,
    editable: true,
    injectCSS: false,
    autofocus: "end",
    onUpdate: ({ editor }) => {
      const updatedText = editor.getText();
      setMessageText(updatedText);
    },
  });

  useEffect(() => {
    if (editor && messageText && messageText !== editor.getText()) {
      editor.commands.setContent(messageText);
    }
  }, [messageText, editor]);

  // Handle initial prompt from Redux (e.g., from Nova AI button click in EmployeeTable)
  useEffect(() => {
    if (initialPrompt && editor) {
      setMessageText(initialPrompt);
      editor.commands.setContent(initialPrompt);
      // Clear the initial prompt from Redux after using it
      dispatch(setNovaInitialPrompt(null));
    }
  }, [initialPrompt, editor, dispatch]);

  useEffect(() => {
    if (!disabled) {
      const timer = setTimeout(() => setDisabled(true), 30000);
      return () => clearTimeout(timer);
    }
  }, [disabled]);

  useEffect(() => {
    if (showHistory) {
      fetchAllConversations();
    }
  }, [showHistory]);

  useEffect(() => {
    if (conversationId) {
      setNewConversationId(conversationId);
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId && !isNewConversation) {
      const stored = conversationMessages[conversationId] || [];
      setLocalMessages(stored);
    } else if (isNewConversation) {
      setLocalMessages([]);
    }
  }, [conversationId, isNewConversation]);

  return {
    propsForNovaAi: {
      disabled,
      setDisabled,
      draggleRef,
      showHistory,
      setShowHistory,
      handleClose,
      handleBackToChat,
      handleShowHistory,
      messageText,
      setMessageText,
      sentMessageText,
      setSentMessageText,
      page,
      setPage,
      isConversationChanged,
      setisConversationChanged,
      showUserMessage,
      setShowUserMessage,
      handleSend,
      editor,
      localMessages,
      setLocalMessages,
      scrollToBottom,
      setScrollToBottom,
    },
  };
};

export default useNovaAiModal;
